import { State } from '@meraki-internal/state';
import { CompositeQuestionProvider } from './question-provider/CompositeQuestionProvider';
import { IMiniWizardConfig } from './IMiniWizardConfig';
import { IQuestion } from './IQuestion';

export class MiniWizardViewModelFactory {
    static inject = () => [CompositeQuestionProvider];
    constructor(public questionProvider: CompositeQuestionProvider){}

    create = (questionnaire: IMiniWizardConfig) => {
        return new MiniWizardViewModel(questionnaire, this);
    };
}

export class MiniWizardViewModel extends State<Record<string, never>> {
    private activeQuestionIndex: number;
    private questions: IQuestion[];

    constructor(private questionnaire: IMiniWizardConfig, private factory: MiniWizardViewModelFactory){
        super({ });

        // we create a copy of questions so that we can mutate questions during a questionnaire
        // there will be pressure on this trade off eventually (eg having a function on a question)
        // but so far it is working to do that stuff in the wizard eg NewCareMapMiniWizard
        // and keep questions simple dumb config / data (separation of concerns)
        // if the pressure pushes this, we'll need a strategy to deep copy so that, as an example
        // disabling a question for mini wizard instance 1 doesn't impact the next instance
        this.questions = JSON.parse(JSON.stringify(this.questionnaire.questions));

        this.activeQuestionIndex = 0;
    }

    getTitle = () => {
        // for now YAGNI, but when we want to support the title changing, consider it happening through onAnswered
        return this.questionnaire.title;
    };

    answer = (value: any) => {

        this.getActiveQuestion().answer = value;

        if (this.questionnaire.onAnswered){
            this.questions = this.questionnaire.onAnswered(this.getActiveQuestion(), this.questions);
        }

        if (this.getActiveQuestion().autoAdvance && this.canGoToNextQuestion()){
            const currentIndex = this.activeQuestionIndex;
            setTimeout(() => {
                // only auto advance if after delay they are still on that question
                // and it is still valid
                if (this.activeQuestionIndex === currentIndex && this.isCurrentStepValid()){
                    this.continue();
                }

            }, 500);
        }

        this.setState({ });
    };

    getActiveQuestion = (): IQuestion => {
        return this.getEnabledQuestions()[this.activeQuestionIndex];
    };

    isLastStep = () => this.activeQuestionIndex === (this.getEnabledQuestions().length - 1);

    canGoBack = () => {
        return this.activeQuestionIndex > 0;
    };

    private canGoToNextQuestion = () => {
        return this.isCurrentStepValid() && !this.isLastStep();
    };

    // this never returns 100 percent complete (not sure if that was on purpose)
    getPercentComplete = () => this.activeQuestionIndex / this.getEnabledQuestions().length;

    isCurrentStepValid = (): boolean => {
        return this.factory.questionProvider.validate(this.getActiveQuestion());
    };

    continue = () => {
        if (!this.isCurrentStepValid()){
            throw new Error('cannot continue while step is invalid');
        }
        if (this.isLastStep()){
            // we are sending along disabled questions on purpose
            // b/c we should persist the answers (eg to restore)
            this.questionnaire.onComplete(this.questions);
        } else {
            const questions = this.getEnabledQuestions();

            this.activeQuestionIndex = this.activeQuestionIndex + 1;
            if (this.activeQuestionIndex >= questions.length){
                this.activeQuestionIndex = questions.length - 1;
            }
            this.setState({});
        }
    };

    private getEnabledQuestions = () => this.questions.filter(q => !q.disabled);

    previous = () => {
        if (!this.canGoBack()){
            throw new Error('cannot go previous');
        }
        this.activeQuestionIndex = this.activeQuestionIndex - 1;
        if (this.activeQuestionIndex < 0){
            this.activeQuestionIndex = 0;
        }
        this.setState({});
    };
}
